import { css } from '@emotion/react';
import styled from '@emotion/styled';

import AccordionBase from 'components/global/Accordion';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';

import { Breakpoints } from 'utils/const.styles';

export const Container = styled.div`
	margin: auto;
	display: flex;
	flex-direction: column;
`;

export const Wrapper = styled.div`
	padding: 3.5rem;
`;

export const Info = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	margin: 1.8rem 0;
	gap: 3.5rem;

	> :last-child {
		span {
			text-align: right;

			body[dir='rtl'] & {
				text-align: left;
			}

			@media ${Breakpoints.mobileLandscape} {
				text-align: left;
			}
		}
	}

	.infoLabel {
		span {
			display: flex;
			flex-direction: column;
			gap: 1rem;
			body[dir='rtl'] & {
				gap: 0;
			}
		}
	}

	&.mobileView {
		flex-direction: column;

		> :last-child span {
			text-align: unset;
		}
	}

	@media ${Breakpoints.mobileLandscape} {
		flex-direction: column;
	}
`;

export const InfoCustomWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const InfoCustomInfo = styled.div`
	display: flex;
	gap: 0.64rem;
`;

export const InfoImage = styled.div`
	width: 5.5rem;
	height: 5.5rem;
	border-radius: var(--small-border-radius);
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
	}
`;
export const InfoContent = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 0.5rem;
	gap: 0.5rem;
	body[dir='rtl'] & {
		margin: 0;
		gap: 0;
	}
`;

export const FormRow = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 1.8rem;
	> .typography {
		white-space: normal;
	}
`;

export const AccordionInner = styled.div`
	display: flex;
	justify-content: space-between;
`;

export const Line = styled.span`
	height: 1px;
	min-height: 1px;
	background-color: var(--border-color);
	width: 100%;
	margin: 2.8rem auto;
	display: block;
`;

export const ItemInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
`;

export const SlotSelectionContainer = styled.div`
	margin: auto;
	display: flex;
	flex-direction: column;
`;

export const DayContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 1.8rem;
	padding: 1.8rem 0;
	@media ${Breakpoints.mobileLandscape} {
		grid-template-columns: repeat(3, 1fr);
	}
	@media ${Breakpoints.mobile} {
		grid-template-columns: repeat(2, 1fr);
	}
	@media (max-width: 355px) {
		grid-template-columns: repeat(1, 1fr);
	}
`;

export const SelectTimeContainer = styled.div`
	margin-top: 3.5rem;
`;

export const DayBox = styled.button`
	width: 100%;
	border: 1px solid ${({ selected }) => (selected ? 'var(--blue)' : 'var(--border-color)')};
	border-radius: var(--small-border-radius);
	padding: 1.8rem;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	gap: 1rem;
	min-height: 8.5rem;
	background-color: ${({ disabled, selected }) =>
		disabled ? 'var(--input-disabled-bg-color)' : selected ? 'var(--blue-10)' : 'inherit'};
	position: relative;
	${({ selected }) =>
		selected &&
		css`
			&::after {
				content: '';
				position: absolute;
				bottom: 0px;
				top: 100%;
				left: 50%;
				margin: auto;
				height: 10px;
				margin-left: -5px;
				width: 10px;
				border: 1px solid transparent;
				transform: rotate(45deg);
				background-color: var(--blue-10);
				border-bottom: 1px solid var(--blue);
				border-left: 1px solid var(--blue);
				transform: rotate(315deg);
			}
			@media ${Breakpoints.mobile} {
				&::after {
					display: none;
				}
			}
		`}
	:disabled {
		cursor: not-allowed;
	}
`;

export const DayBadge = styled.div`
	background-color: ${({ disabled, selected }) =>
		disabled ? 'var(--black-20)' : selected ? 'var(--blue)' : 'var(--blue-10)'};
	border-radius: 2.15rem;
	padding: 0.5rem 1rem;
	& > .typography {
		white-space: nowrap;
	}
`;

export const BasicLine = styled.span`
	height: 1px;
	min-height: 1px;
	background-color: var(--border-color);
	width: 100%;
	display: block;
`;

export const SlotBox = styled.button`
	border: 1px solid ${({ selected }) => (selected ? 'var(--blue)' : 'var(--border-color)')};
	border-radius: var(--small-border-radius);
	padding: 1.8rem;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	background-color: ${({ selected }) => (selected ? 'var(--blue-10)' : 'inherit')};
	transition: background-color 0.2s ease;
	&:hover {
		background-color: ${({ selected }) => !selected && 'rgb(0 0 0 / 5%)'};
	}
	:disabled {
		cursor: not-allowed;
		border-color: var(--border-color);
		background-color: var(--input-disabled-bg-color);
		color: var(--content-4);
		-webkit-text-fill-color: var(--content-4);
	}
`;

export const TitleBox = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 1rem;
`;

export const DaySlotContainer = styled.div`
	margin: 3.5rem 0 1rem 0;
`;

export const SlotContainer = styled.div`
	display: grid;
	gap: 1.8rem;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	margin-bottom: 1.8rem;

	@media ${Breakpoints.mobile} {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
`;

export const SuccessWrapper = styled.div`
	padding: 3.5rem;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

export const ProductImage = styled(Image)`
	max-width: 5.5rem;
	max-height: 5.5rem;
	object-fit: contain;
	border-radius: var(--small-border-radius);
`;

export const Link = styled.a`
	text-decoration: none;
`;

export const Accordion = styled(AccordionBase)`
	width: 100%;
	margin-top: 3.5rem;
`;

export const SlotTitle = styled(Typography)`
	min-width: max-content;
	margin-top: 0 !important;
	margin-bottom: 0 !important;
`;

export const Bold = styled.span`
	font-weight: 700;
`;
